import React from "react";
import { Typography, Box } from "@mui/material";
import SimplePaper from "../../../components/SimplePaper";
import { MathComponent } from "mathjax-react";

const D5fromD1 = () => {
  const title = "Calculation of D5 (Panchamsa) from D1 (Rasi)";

  const body = (
    <>
      <Typography variant="body1" paragraph>
        The D5 chart, or Panchamsa chart, is used in Vedic astrology to analyze
        creativity, intelligence, spiritual inclinations, and future prospects.
        Below is the step-by-step process to calculate the D5 chart from the D1
        (Rasi) chart.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Procedure to Calculate D5 (Panchamsa) from D1 (Rasi)
      </Typography>

      <Typography variant="h5" gutterBottom>
        1. Identify the Planet's Degree in the D1 Chart
      </Typography>

      <Typography variant="body1" paragraph>
        First, identify the degree of the planet in the D1 chart. For example,
        if Jupiter is located at 14° Capricorn, this degree will be used for the
        calculation.
      </Typography>

      <Typography variant="h5" gutterBottom>
        2. Divide the Sign into Five Parts
      </Typography>

      <Typography variant="body1" paragraph>
        Each zodiac sign is divided into five equal parts, each spanning 6°. To
        find the Panchamsa position of a planet, we divide its degree in the
        sign by 6.
      </Typography>

      <Box sx={{ textAlign: "center", margin: "1rem 0" }}>
        <MathComponent
          tex={String.raw`Panchamsa \ Position = \frac{Degree \ in \ Sign}{6}`}
        />
      </Box>

      <Typography variant="body1" paragraph>
        Depending on the result, we can determine the section in which the
        planet lies:
      </Typography>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`0 \leq \frac{Degree \ in \ Sign}{6} < 1`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>first 6°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`1 \leq \frac{Degree \ in \ Sign}{6} < 2`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>second 6°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`2 \leq \frac{Degree \ in \ Sign}{6} < 3`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>third 6°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`3 \leq \frac{Degree \ in \ Sign}{6} < 4`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>fourth 6°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`4 \leq \frac{Degree \ in \ Sign}{6} < 5`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>fifth 6°</strong>.
        </Typography>
      </Box>

      <Typography variant="h5" gutterBottom>
        3. Assign the Panchamsa Based on the Planet's Degree
      </Typography>

      <Typography variant="body1" paragraph>
        <strong>First 6°:</strong> The planet is assigned to the{" "}
        <strong>same sign</strong> as the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Second 6°:</strong> The planet is assigned to the{" "}
        <strong>5th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Third 6°:</strong> The planet is assigned to the{" "}
        <strong>9th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Fourth 6°:</strong> The planet is assigned to the{" "}
        <strong>11th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Fifth 6°:</strong> The planet is assigned to the{" "}
        <strong>3rd sign</strong> from the D1 sign.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Example: Calculating Jupiter at 14° Capricorn
      </Typography>

      <Typography variant="body1" paragraph>
        Capricorn is the D1 sign. Jupiter at 14° falls in the third 6° (between
        12° and 18°). Therefore, it belongs to the <strong>9th sign</strong>{" "}
        from Capricorn, which is <strong>Virgo</strong>
      </Typography>

      <Typography variant="h4" gutterBottom>
        Python Program to Calculate D5 (Panchamsa) from D1
      </Typography>

      <Typography variant="body1" paragraph>
        Below is a Python program that performs the calculation of the D5
        (Panchamsa) position from the D1 (Rasi) chart, using the planet's degree
        and sign in the D1 chart.
      </Typography>

      <Box
        component="pre"
        sx={{
          padding: "1rem",
          backgroundColor: "#f5f5f5",
          borderRadius: "5px",
        }}
      >
        <code>
          {`def calculate_panchamsa(degree, sign):
    # List of zodiac signs in order
    zodiac_signs = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces']

    # Get the index of the current sign in the zodiac cycle
    sign_index = zodiac_signs.index(sign)

    # Determine which Panchamsa the planet is in based on its degree
    if degree <= 6:
        # First 6° belongs to the same sign
        panchamsa_sign = zodiac_signs[sign_index]
    elif 6 < degree <= 12:
        # Second 6° belongs to the 5th sign from the current sign
        panchamsa_sign = zodiac_signs[(sign_index + 4) % 12]
    elif 12 < degree <= 18:
        # Third 6° belongs to the 9th sign from the current sign
        panchamsa_sign = zodiac_signs[(sign_index + 8) % 12]
    elif 18 < degree <= 24:
        # Fourth 6° belongs to the 11th sign from the current sign
        panchamsa_sign = zodiac_signs[(sign_index + 10) % 12]
    else:
        # Fifth 6° belongs to the 3rd sign from the current sign
        panchamsa_sign = zodiac_signs[(sign_index + 2) % 12]

    return panchamsa_sign

# Example usage
planet = "Jupiter"
degree = 14
sign = "Capricorn"

panchamsa_sign = calculate_panchamsa(degree, sign)
print(f"{planet} at {degree}° {sign} in the D1 chart is placed in {panchamsa_sign} in the D5 chart.")`}
        </code>
      </Box>

      <Typography variant="body1" paragraph>
        The above Python program calculates the Panchamsa for a planet based on
        its degree and sign in the D1 chart. The program divides the sign into
        five equal parts and assigns the Panchamsa based on the planet's degree.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Explanation of the Formula Used in the Python Program
      </Typography>

      <Typography variant="body1" paragraph>
        The program divides the sign into five parts: the first 6°, the second
        6°, the third 6°, the fourth 6°, and the fifth 6°. It assigns the
        Panchamsa based on the planet's degree within the sign:
        <br />
        - First 6° belongs to the same sign.
        <br />
        - Second 6° belongs to the 5th sign from the D1 sign.
        <br />
        - Third 6° belongs to the 9th sign from the D1 sign.
        <br />
        - Fourth 6° belongs to the 11th sign from the D1 sign.
        <br />- Fifth 6° belongs to the 3rd sign from the D1 sign.
      </Typography>

      <Typography variant="body1" paragraph>
        This method programmatically reproduces the manual calculation and can
        be easily adjusted for different planets, degrees, and signs.
      </Typography>
    </>
  );

  return <SimplePaper title={title} body={body} />;
};

export default D5fromD1;
